import React from 'react';
import fr from 'facet-rest';
import PropTypes from 'prop-types';
import Button from 'Components/UI/Button';
import DocumentList from 'Components/UI/DocumentList';
import { Box } from '@material-ui/core';
import { withStyles } from 'Components/ComponentLibrary/ExternalLibraries/Libraries/MaterialV4/styles';
import Typography from 'Components/ComponentLibrary/ExternalLibraries/Libraries/MaterialV4/Typography';
import useMetaLayoutContext from 'Components/UI/Meta/MetaForm/Components/MetaLayoutContext';
import { noop } from 'Helpers/PropTypes';
import { AddCircle } from '@material-ui/icons';
import { CollectionProvider, useCollection } from 'Context/CollectionContext';
import CardList from 'Components/UI/CardList';
import LoadingOverlay from 'Components/UI/LoadingOverlay/LoadingOverlay';

const styles = theme => ({
  root: {},
  header: {
    display: 'flex',
    width: '100%',
    flexDirection: 'row',
    flex: '1 1 auto',
    alignItems: 'center',
    justifyContent: 'space-between',
  },
  text: {
    fontWeight: theme.typography.fontWeightBold,
  },
  uploadSection: {
    display: 'flex',
    flexDirection: 'column',
    margin: theme.spacing(4, 0),
  },
  trustInformation: {
    display: 'flex',
    flexDirection: 'column',
    flex: '1 1 auto',
  },
  sendButton: {
    borderRadius: theme.radius.sm,
    margin: 'auto',
    display: 'flex',
    alignItems: 'center',
    padding: theme.spacing(1),
    minWidth: theme.dimensions.sm,
  },
});

const AdditionalTrusteeCardList = ({ onClick, ...rest }) => {
  const { collection } = useCollection();

  const canAdd = collection !== null && collection?.resources().length() < 3;

  return (
    <CardList
      onAdd={!canAdd ? null : () => onClick()}
      onClick={onClick}
      {...rest}
    />
  );
};

const SingleCardList = ({ onClick, ...rest }) => {
  const { collection } = useCollection();

  const canAdd = collection !== null && collection?.resources().length() === 0;

  return (
    <CardList
      onAdd={!canAdd ? null : () => onClick()}
      onClick={onClick}
      {...rest}
    />
  );
};


const TrustApproval = ({
  classes,
  onUpdateTrust,
  onUpdateTrustee,
  onUpdateAdditionalTrustees,
  onTrustUpload,
  onOtherUpload,
  onLoad,
  onError,
}) => {
  const {
    resource: approval,
    onSubmit,
    saving,
  } = useMetaLayoutContext();
  const missingTrustDoc = approval.attr('trustCertificationDocument')
    .resources()
    .length() === 0;

  const hasTrustRel = Boolean(approval?.relationship(fr.relationships.trust.name)
    ?.link('related'));
  const hasTrusteeRel = Boolean(approval?.relationship(fr.relationships.trustee.name)
    ?.link('related'));
  const hasAdditionalTrusteeRel = Boolean(approval?.relationship(fr.relationships.additionalTrustees.name)
    ?.link('related'));

  const canPost = approval.id() > 0 && approval.meta()
    ?.action('POST');

  return (
    <Box>
      {saving && <LoadingOverlay />}
      <Box className={classes.uploadSection}>
        <Box className={classes.header}>
          <Typography component="p">
            {'Trust Document'}
          </Typography>
          <Button
            size="small"
            startIcon={<AddCircle color="secondary" />}
            onClick={onTrustUpload}
            disabled={!missingTrustDoc}
          >
            {'Add Trust Document'}
          </Button>
        </Box>
        <DocumentList
          documentCollection={approval.attr('trustCertificationDocument')}
          onFinally={onLoad}
          onError={onError}
        />
      </Box>
      <Box className={classes.uploadSection}>
        <Box className={classes.header}>
          <Typography component="p">
            {'Supporting Documents'}
          </Typography>
          <Button
            size="small"
            startIcon={<AddCircle color="secondary" />}
            onClick={onOtherUpload}
          >
            {'Add Files'}
          </Button>
        </Box>
        <DocumentList
          documentCollection={approval.attr('accountDocuments')}
          onFinally={onLoad}
          onError={onError}
        />
      </Box>
      <Box className={classes.trustInformation}>
        {
          hasTrustRel && (
            <CollectionProvider link={approval?.relationship(fr.relationships.trust.name)
              .link('related')
              .url()}
            >
              <SingleCardList
                paddingY={2}
                label="Trust"
                addLabel="Add Trust"
                onClick={onUpdateTrust}
                renderPrimary={(r) => `${r.attr('trustName')}`}
              />
            </CollectionProvider>
          )
        }
        {
          hasTrusteeRel && (
            <CollectionProvider link={approval?.relationship(fr.relationships.trustee.name)
              .link('related')
              .url()}
            >
              <SingleCardList
                paddingY={2}
                label="Primary Trustee"
                addLabel="Add Primary Trustee"
                onClick={onUpdateTrustee}
                renderPrimary={(r) => `${r.attr('trusteeFirstName')} ${r.attr('trusteeLastName')}`}
              />
            </CollectionProvider>
          )
        }
        {
          hasAdditionalTrusteeRel && (
            <CollectionProvider link={approval?.relationship(fr.relationships.additionalTrustees.name)
              .link('related')
              .url()}
            >
              <AdditionalTrusteeCardList
                addLabel="Add Trustee"
                paddingY={2}
                label="Additional Trustees"
                onClick={onUpdateAdditionalTrustees}
                renderPrimary={(r) => `${r.attr('familyName')} ${r.attr('givenName')}`}
              />
            </CollectionProvider>
          )
        }
      </Box>
      <Button
        disabled={saving || !canPost}
        variant="contained"
        color="primary"
        className={classes.sendButton}
        onClick={onSubmit}
      >
        {hasTrustRel && hasTrusteeRel ? 'Approve & Send Application' : 'Send an alert to Investment Ops'}
      </Button>
    </Box>
  );
};

TrustApproval.defaultProps = {
  onOtherUpload: noop,
  onTrustUpload: noop,
  onLoad: noop,
  onError: noop,
  onUpdateTrust: noop,
  onUpdateTrustee: noop,
  onUpdateAdditionalTrustees: noop,
};

TrustApproval.propTypes = {
  onUpdateTrust: PropTypes.func,
  onUpdateTrustee: PropTypes.func,
  onUpdateAdditionalTrustees: PropTypes.func,
  onError: PropTypes.func,
  onOtherUpload: PropTypes.func,
  onTrustUpload: PropTypes.func,
  onLoad: PropTypes.func,
  classes: PropTypes.object.isRequired,
};

export default withStyles(styles)(TrustApproval);
